import { useContext, useMemo, useState, MouseEvent } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  IconButton,
  type SxProps,
  type Theme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Unarchive';
import type { Company, Group, User } from '@esg/esg-global-types';

import { canManageUser } from '../../util/user_access';
import { UserContext } from '../../context/UserContext';
import { useUserMutations } from './hooks/useUserMutations';

interface BulkActionBarProps {
  /**
   * Full data for the currently-selected users, sourced from the host's
   * selection map (not a rows[] + ids lookup) — so a selected user still
   * resolves correctly even after they scroll off the loaded page.
   */
  selectedUsers: User[];
  groups: Group[];
  /** Called after a bulk op completes so the host can refetch. */
  onAfterAction: () => void;
  /** Called by the host to clear the selection. */
  onClear: () => void;
}

const barSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 2,
  py: 1,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  borderRadius: 1,
  mb: 1,
  flexWrap: 'wrap'
};

interface PopupState {
  anchor: HTMLElement | null;
  kind: 'company' | null;
}

/** Sticky bulk-action strip shown above the DataGrid when rows are selected. */
const BulkActionBar = ({ selectedUsers, groups, onAfterAction, onClear }: BulkActionBarProps) => {
  const { user: currentUser } = useContext(UserContext);
  const mutations = useUserMutations();
  const [popup, setPopup] = useState<PopupState>({ anchor: null, kind: null });
  const [resending, setResending] = useState<boolean>(false);

  const openPopup = (event: MouseEvent<HTMLElement>, kind: 'company') =>
    setPopup({ anchor: event.currentTarget, kind });
  const closePopup = () => setPopup({ anchor: null, kind: null });

  const selectedIds: string[] = useMemo(() => selectedUsers.map((u) => u.id), [selectedUsers]);

  const unmanageableCount: number = useMemo(
    () => selectedUsers.filter((user) => !canManageUser(currentUser, user)).length,
    [selectedUsers, currentUser]
  );

  const anyDeleted = selectedUsers.some((user) => !!user.deleted);
  const allDeleted = selectedUsers.length > 0 && selectedUsers.every((user) => !!user.deleted);

  const actionDisabledReason: string | null = (() => {
    if (selectedUsers.length === 0) return 'No users selected';
    if (unmanageableCount > 0) {
      return `${unmanageableCount} of ${selectedUsers.length} can't be managed by you`;
    }
    return null;
  })();
  const isDisabled = actionDisabledReason !== null;

  const allCompanies: Array<{ company: Company; group: Group }> = useMemo(
    () => groups.flatMap((group) => (group.companies ?? []).map((company) => ({ company, group }))),
    [groups]
  );

  if (selectedUsers.length === 0) return null;

  const handleAddToCompany = async (group_id: string, company_id: string, role: string) => {
    closePopup();
    await mutations.bulkUpdateAccess({
      ids: selectedIds,
      target_group_id: group_id,
      target_company_id: company_id,
      role
    });
    onAfterAction();
  };

  const handleResend = async () => {
    const inviteRows = selectedUsers.map((user) => ({
      id: user.id,
      name: user.first_name,
      email: user.email
    }));
    setResending(true);
    try {
      await mutations.bulkResendInvite(inviteRows);
    } finally {
      setResending(false);
    }
    onAfterAction();
  };

  const handleDelete = async () => {
    if (allDeleted) {
      await mutations.bulkRestore(selectedIds);
    } else {
      await mutations.bulkSoftDelete(selectedIds);
    }
    onAfterAction();
    onClear();
  };

  return (
    <Box sx={barSx} role="region" aria-label="Bulk actions">
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {selectedUsers.length} selected
      </Typography>
      <Tooltip title="Clear selection">
        <IconButton size="small" onClick={onClear} sx={{ color: 'inherit' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1 }} />

      <Tooltip title={actionDisabledReason ?? 'Grant access to a company for the selected users'}>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => openPopup(e, 'company')}
            disabled={isDisabled || anyDeleted}
          >
            Add to company
          </Button>
        </span>
      </Tooltip>

      <Tooltip title={actionDisabledReason ?? 'Re-send the invitation email'}>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<EmailIcon />}
            onClick={handleResend}
            disabled={isDisabled || anyDeleted || resending}
          >
            {resending ? 'Sending…' : 'Resend invite'}
          </Button>
        </span>
      </Tooltip>

      <Tooltip
        title={
          actionDisabledReason ??
          (allDeleted ? 'Restore the selected users' : 'Soft-delete the selected users')
        }
      >
        <span>
          <Button
            size="small"
            variant="contained"
            color={allDeleted ? 'success' : 'error'}
            startIcon={allDeleted ? <RestoreIcon /> : <DeleteIcon />}
            onClick={handleDelete}
            disabled={isDisabled}
          >
            {allDeleted ? 'Restore' : 'Delete'}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={popup.kind === 'company' ? popup.anchor : null}
        open={popup.kind === 'company'}
        onClose={closePopup}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}
      >
        {allCompanies.map(({ company, group }) => (
          <MenuItem
            key={company.id}
            onClick={() => handleAddToCompany(group.id, company.id, 'viewer')}
          >
            {group.name} · {company.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default BulkActionBar;
